"use client";

import { isEmpty, sum } from "lodash-es";
import { useStockDetailsHandle } from "@hooks/useStockDetailsHandle";
import { useStockDetailsMarket } from "@hooks/useStockDetailsMarket";

import ChartRating from "@components/DetailsCommon/Forecast/PricePredictionSummary/ChartRating";
import { FORECAST_QUERY_PARAMS } from "@config/stock";
import ForecastCard from "@components/DetailsCommon/Forecast/ForecastCard";
import ProgressRating from "@components/DetailsCommon/Forecast/PricePredictionSummary/ProgressRating";
import React from "react";
import { calculatePercentage } from "@components/DetailsCommon/Forecast/utils/calculatePercentage";
import { recommendationMap } from "@components/DetailsCommon/Forecast/constants";
import { ViewDetailsLink, buildStockDetailModuleHref } from "./ViewDetailsLink";

interface AssetAnalystRatingBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetAnalystRatingBusinessChart: React.FC<
  AssetAnalystRatingBusinessChartProps
> = ({ assetType, assetRouterId }) => {
  const isStock = assetType === "stock";

  const { data: stockData, isLoading } = useStockDetailsMarket({
    queryParams: { ...FORECAST_QUERY_PARAMS, ids: [assetRouterId] },
    queryKey: ["chat-analyst-rating", assetRouterId],
  });

  const stockSymbol = stockData?.symbol || "";

  const { recommendationTrend } = useStockDetailsHandle({
    tokenList: stockData,
    symbol: stockSymbol,
  });

  if (isLoading) {
    return (
      <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  const { recommendation, trend } = recommendationTrend || {};
  if (isEmpty(recommendationTrend) || !recommendation || !isStock) {
    return null;
  }

  const trendItem = trend?.[0];
  const { strong_buy, buy, hold, sell, strong_sell } = trendItem || {};
  const sumValue = sum([strong_buy, buy, hold, sell, strong_sell]);
  const recommendationText =
    recommendationMap[recommendation as keyof typeof recommendationMap] || "--";

  const items = [
    {
      label: "Strong buy",
      value: calculatePercentage(strong_buy ?? 0, sumValue),
      count: strong_buy ?? 0,
    },
    {
      label: "Buy",
      value: calculatePercentage(buy ?? 0, sumValue),
      count: buy ?? 0,
    },
    {
      label: "Hold",
      value: calculatePercentage(hold ?? 0, sumValue),
      count: hold ?? 0,
    },
    {
      label: "Sell",
      value: calculatePercentage(sell ?? 0, sumValue),
      count: sell ?? 0,
    },
    {
      label: "Strong sell",
      value: calculatePercentage(strong_sell ?? 0, sumValue),
      count: strong_sell ?? 0,
    },
  ];

  return (
    <>
      <div className="w-full overflow-hidden">
        <ForecastCard>
          <div className="flex flex-row gap-10 w-full max-md:flex-col justify-center items-center border border-border_solid rounded-2 py-3 md:p-4 md:h-40">
            <ChartRating verdict={recommendationText} />
            <ProgressRating items={items} />
          </div>
        </ForecastCard>
      </div>
      {stockSymbol && (
        <ViewDetailsLink
          href={buildStockDetailModuleHref(stockSymbol, "forecast")}
        />
      )}
    </>
  );
};
